/**
 * ================================================================
 *  CART GIFT ADD-ON — Add + Remove buttons
 *  File: assets/cart-gift-addon.js
 *  Theme: Shopify Refresh 11.0.0
 *
 *  FIX: /cart/change.js needs `line` (1-based index) NOT variant id
 *  FIX: Add `_gift_addon` attribute to exclude from discounts
 *  FIX: Block automatic discounts on gift products
 * ================================================================
 */

(function () {
  "use strict";
  const SEL = {
    wrap:       "#cart-gift-addon",
    addBtn:     "#cart-gift-addon-add",
    removeBtn:  "#cart-gift-addon-remove",
    addedWrap:  "#cart-gift-addon-added-wrap",
  };
  
  // ─── GLOBAL: Block automatic discounts on gift items ──────────
  window.GIFT_ADDON_VARIANT_IDS = new Set();
  
  // ─── Cart API helpers ────────────────────────────────────────
  async function cartPost(url, body) {
    const res = await fetch(url, {
      method:  "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body:    JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || `Cart API ${url} → ${res.status}`);
    }
    return res.json();
  }
  
  async function getCart() {
    const res = await fetch("/cart.js", { headers: { Accept: "application/json" } });
    return res.json();
  }
  
  // ─── Find line index of gift variant in cart (1-based) ──────
  // Shopify /cart/change.js needs `line` = position in cart.items array
  async function findGiftLineIndex(variantId) {
    const cart = await getCart();
    const idx  = cart.items.findIndex((item) => item.variant_id === variantId);
    return idx === -1 ? null : idx + 1; // 1-based
  }
  
  // ─── Update header count badge ───────────────────────────────
  function updateHeaderCount(count) {
    document.querySelectorAll("cart-icon-bubble .visually-hidden").forEach((el) => {
      if (/\d/.test(el.textContent)) {
        el.textContent = `${count} item${count !== 1 ? "s" : ""}`;
      }
    });
    document.querySelectorAll(".cart-count-bubble span:not(.visually-hidden)").forEach((el) => {
      el.textContent = count;
    });
    const bubble = document.querySelector(".cart-count-bubble");
    if (bubble) bubble.style.display = count > 0 ? "" : "none";
  }

  // ─── Switch UI between add / remove state ────────────────────
  function setUIState(inCart) {
    const addBtn    = document.querySelector(SEL.addBtn);
    const addedWrap = document.querySelector(SEL.addedWrap);
    const wrap      = document.querySelector(SEL.wrap);
    if (!addBtn || !addedWrap || !wrap) return;

    wrap.dataset.inCart = inCart ? "true" : "false";

    if (inCart) {
      addBtn.classList.add("cart-gift-addon__btn--hidden");
      addedWrap.classList.remove("cart-gift-addon__btn--hidden");
    } else {
      addBtn.classList.remove("cart-gift-addon__btn--hidden");
      addedWrap.classList.add("cart-gift-addon__btn--hidden");
    }
  }

  // ─── Re-render Refresh cart drawer via Sections API ─────────
  async function refreshDrawer() {
    try {
      const res  = await fetch("/?sections=cart-drawer", {
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });
      const data = await res.json();
      const html = data["cart-drawer"];
      if (!html) return;

      const parser   = new DOMParser();
      const newDoc   = parser.parseFromString(html, "text/html");
      const newInner = newDoc.querySelector("#CartDrawer");
      const curInner = document.querySelector("#CartDrawer");

      if (newInner && curInner) {
        curInner.innerHTML = newInner.innerHTML;
        attachListeners();
        // Re-mark gift items after refresh
        markGiftItemsInDOM();
      }
    } catch (e) {
      console.warn("[GiftAddon] Drawer refresh error:", e);
    }
  }

  // ─── Mark gift items in DOM (prevent CSS discount styling) ────
  function markGiftItemsInDOM() {
    const cart = document.querySelector('[data-cart-items]');
    if (!cart) return;

    const items = cart.querySelectorAll('[data-line-item]');
    items.forEach((item) => {
      const variantId = parseInt(item.dataset.variantId, 10);
      if (window.GIFT_ADDON_VARIANT_IDS.has(variantId)) {
        item.classList.add("cart-item--gift-addon");
        item.dataset.giftAddon = "true";
        // Hide discount badges on gift items
        const discountBadge = item.querySelector('.discount-badge, .sale-badge');
        if (discountBadge) discountBadge.style.display = 'none';
      }
    });
  }

  // ─── ADD gift ────────────────────────────────────────────────
  async function addGift() {
    const wrap   = document.querySelector(SEL.wrap);
    const addBtn = document.querySelector(SEL.addBtn);
    if (!wrap || !addBtn) return;

    const variantId = parseInt(wrap.dataset.variantId, 10);
    addBtn.classList.add("cart-gift-addon__btn--loading");

    try {
      // Add to global set for discount exclusion
      window.GIFT_ADDON_VARIANT_IDS.add(variantId);
      
      // /cart/add.js accepts id as variant ID — this is correct
      // Add _gift_addon attribute so discount logic can exclude it
      await cartPost("/cart/add.js", { 
        items: [{ 
          id: variantId, 
          quantity: 1,
          properties: {
            "_gift_addon": "true"
          }
        }] 
      });
      
      const cart = await getCart();

      setUIState(true);
      updateHeaderCount(cart.item_count);
      
      // Dispatch event with gift info
      document.dispatchEvent(new CustomEvent("cart:updated", { 
        detail: { 
          cart,
          giftAddonAdded: variantId 
        } 
      }));
      
      await refreshDrawer();
      markGiftItemsInDOM();

    } catch (err) {
      console.error("[GiftAddon] Add failed:", err);
      window.GIFT_ADDON_VARIANT_IDS.delete(variantId);
      alert("Could not add item. Please try again.");
    } finally {
      const btn = document.querySelector(SEL.addBtn);
      if (btn) btn.classList.remove("cart-gift-addon__btn--loading");
    }
  }

  // ─── REMOVE gift ─────────────────────────────────────────────
  async function removeGift() {
    const wrap      = document.querySelector(SEL.wrap);
    const removeBtn = document.querySelector(SEL.removeBtn);
    if (!wrap || !removeBtn) return;

    const variantId = parseInt(wrap.dataset.variantId, 10);
    removeBtn.classList.add("cart-gift-addon__btn--loading");

    try {
      // CORRECT: /cart/change.js needs `line` (1-based position), NOT `id`
      const lineIndex = await findGiftLineIndex(variantId);

      if (lineIndex === null) {
        // Already not in cart — just update UI
        setUIState(false);
        window.GIFT_ADDON_VARIANT_IDS.delete(variantId);
        return;
      }

      await cartPost("/cart/change.js", {
        line:     lineIndex,   // ← FIX: use line index, not variant id
        quantity: 0,
      });

      const cart = await getCart();
      window.GIFT_ADDON_VARIANT_IDS.delete(variantId);

      setUIState(false);
      updateHeaderCount(cart.item_count);
      
      document.dispatchEvent(new CustomEvent("cart:updated", { 
        detail: { 
          cart,
          giftAddonRemoved: variantId 
        } 
      }));
      
      await refreshDrawer();

    } catch (err) {
      console.error("[GiftAddon] Remove failed:", err);
      alert("Could not remove item. Please try again.");
    } finally {
      const btn = document.querySelector(SEL.removeBtn);
      if (btn) btn.classList.remove("cart-gift-addon__btn--loading");
    }
  }

  // ─── Attach listeners ────────────────────────────────────────
  function attachListeners() {
    const addBtn    = document.querySelector(SEL.addBtn);
    const removeBtn = document.querySelector(SEL.removeBtn);

    if (addBtn && !addBtn._giftBound) {
      addBtn._giftBound = true;
      addBtn.addEventListener("click", addGift);
    }
    if (removeBtn && !removeBtn._giftBound) {
      removeBtn._giftBound = true;
      removeBtn.addEventListener("click", removeGift);
    }
  }

  // ─── Load existing gift items on page load ───────────────────
  async function loadExistingGiftItems() {
    try {
      const cart = await getCart();
      cart.items.forEach((item) => {
        if (item.properties && item.properties._gift_addon === "true") {
          window.GIFT_ADDON_VARIANT_IDS.add(item.variant_id);
        }
      });
      markGiftItemsInDOM();
    } catch (e) {
      console.warn("[GiftAddon] Could not load existing gift items:", e);
    }
  }

  // ─── Init ────────────────────────────────────────────────────
  function init() {
    loadExistingGiftItems();
    attachListeners();
    document.addEventListener("cart:updated", () => {
      setTimeout(() => {
        attachListeners();
        markGiftItemsInDOM();
      }, 350);
    });
  }

  document.readyState === "loading"
    ? document.addEventListener("DOMContentLoaded", init)
    : init();

})();
